import {
  CalendarDays, Check, ChevronDown, LogOut, MessageSquare, Rows3, Search, ShieldCheck, UserRound, UsersRound,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/auth/authState";
import type { Corporation } from "@/api/corporations";
import { DataVersionStatus } from "@/components/state/DataVersionStatus";
import { Segmented } from "@/components/ui/Segmented";
import { FeedbackButton } from "@/feedback/FeedbackButton";
import { searchParamsFromFilters } from "@/lib/assetFilters";
import { REPORTING_YEAR } from "@/lib/reportingYear";

import { useDensity, useViewport } from "./shellPreferences";
import { useDismissable } from "./useDismissable";
import "./Header.css";

type HeaderProps = {
  corporations: Corporation[];
  selected: Corporation | null;
  loading: boolean;
  onSelect: (id: string) => void;
};

/**
 * De kop van stijl A (ontwerp 28-10-2026): geen navy balk meer maar een lichte
 * rij op de ondergrond. Links zoeken, rechts als chips: dataversie (Actueel),
 * feedback, het verslagjaar, het scenario, en dan de corporatie direct links
 * van de gebruiker (besluit Tom, 28-10-2026). Dat vervangt de plaatsing van
 * FB-065/F4, waarbij de kiezer linksboven tegen het merkblok stond.
 *
 * Wat uit de eerdere plaatsingen (hieronder) blijft gelden: het logo van de
 * corporatie hoort bij de selector, niet los in de zijbalk.
 *
 * Vrijwel elk prototypescherm heeft een portefeuilleselector, dus staat hij in
 * de schil en niet per scherm. De keuze zit in de schil-state; straks bepaalt
 * hij welke corporatie de API-aanroepen meekrijgen.
 *
 * Het huisstijllogo van de klant staat hier (Tom, 21-09-2026) en niet meer
 * onderin de zijbalk: het hoort bij de portefeuille die je gekozen hebt, en
 * dat is precies wat deze selector zegt. Onderin een navy kolom stond het
 * bovendien los van alles, met de naam er nog eens onder.
 *
 * Van 23-09 tot 27-10-2026 stond de selector rechts, naast de feedbackknop
 * (Jan, FB-049). Sinds besluit F4 (FB-065, 27-10-2026) staat hij links, direct
 * tegen het merkblok van de zijbalk aan: linksboven leest één rij als
 * Q-Estate · Portefeuille Atlas · logo van de corporatie. Het logo verhuist,
 * het staat niet twee keer in beeld.
 */
export function Header({ corporations, selected, loading, onSelect }: HeaderProps) {
  return (
    <header className="atl-header">
      <GlobalSearch />
      <div className="atl-header__spacer" />
      {/* Of de cijfers op het scherm bij de laatste dataversie horen (plan 17, E4). */}
      <DataVersionStatus />
      {/* De feedbackknop staat in de schil en niet per scherm: hij hoort overal
          te werken, en de context van het scherm komt uit `useFeedbackContext`. */}
      <FeedbackButton />
      {/* v4.0 §3: het verslagjaar staat permanent in de kop. */}
      <span className="atl-header__chip atl-header__year" title="Alle cijfers gaan over dit verslagjaar">
        <CalendarDays size={15} strokeWidth={1.75} aria-hidden />
        <small>Verslagjaar</small> <span className="atl-numeric">{REPORTING_YEAR}</span>
      </span>
      {/* Er is nog geen scenario te kiezen: alle cijfers zijn de basis. De chip
          zegt dat, zonder te doen alsof er een keuzelijst achter zit. */}
      <span className="atl-header__chip atl-header__scenario" title="Alle cijfers zijn het basisscenario; scenario's kiezen volgt met het scherm Scenario's">
        <small>Scenario</small> Basis
      </span>
      {/* Corporatie direct links van de gebruiker (besluit Tom, 28-10-2026): de
          rechterkant leest als "voor welke corporatie" en "wie". */}
      <PortfolioPicker
        corporations={corporations}
        selected={selected}
        loading={loading}
        onSelect={onSelect}
      />
      <UserMenu />
    </header>
  );
}

/* ----------------------------------------------------------------- zoeken */

/**
 * Zoeken vanuit elk scherm (v4.0 §3). Eerste versie: de zoekterm gaat naar de
 * objectenlijst, die op adres, postcode, VHE-nummer en BAG-sleutel zoekt
 * (`asset_filters`, zoekterm). Complexen, panden, wijken en gemeenten volgen
 * met een eigen zoekroute die alle niveaus samen doorzoekt.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  // Op een smal venster een kortere plaatshouder, zodat hij niet wordt afgekapt
  // (ronde 5, S2). Het label voor de schermlezer blijft volledig.
  const smal = useViewport() === "narrow";
  const [term, setTerm] = useState("");
  const veld = useRef<HTMLInputElement>(null);
  // ⌘K (of Ctrl+K) zet de cursor in het zoekveld, vanuit elk scherm (ontwerp 28-10-2026).
  useEffect(() => {
    const opToets = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        veld.current?.focus();
      }
    };
    window.addEventListener("keydown", opToets);
    return () => window.removeEventListener("keydown", opToets);
  }, []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const zoek = term.trim();
    if (!zoek) return;
    navigate(`/objecten?${searchParamsFromFilters({ search: zoek })}`);
  };
  return (
    <form className="atl-header__search" role="search" onSubmit={submit}>
      <Search size={15} strokeWidth={1.75} aria-hidden />
      <input
        ref={veld}
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={smal ? "Zoek adres, postcode, VHE of BAG-ID" : "Zoek op adres, postcode, VHE of BAG-ID"}
        aria-label="Zoek een object op adres, postcode, VHE-nummer of BAG-ID"
        aria-keyshortcuts="Meta+K Control+K"
      />
      <kbd className="atl-header__kbd" aria-hidden>
        ⌘K
      </kbd>
    </form>
  );
}

/* --------------------------------------------------------------- selector */

function PortfolioPicker({
  corporations,
  selected,
  loading,
  onSelect,
}: {
  corporations: Corporation[];
  selected: Corporation | null;
  loading: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissable(ref, open, close);

  // Eén corporatie is het normale geval: de leeslaag eist bij een ontbrekende
  // `corporation_id` dat de tenant er precies één heeft. Dan is er niets te
  // kiezen, en een keuzelijst met één regel doet alsof van wel.
  const kiesbaar = corporations.length > 1;

  if (!kiesbaar) {
    return (
      <div className="atl-header__menu atl-header__menu--portfolio">
        <span className="atl-header__trigger atl-header__trigger--static">
          <span className="atl-sr-only">Portefeuille</span>
          <CorporationMark corporation={selected} loading={loading} />
        </span>
      </div>
    );
  }

  return (
    <div className="atl-header__menu atl-header__menu--portfolio" ref={ref}>
      <button
        type="button"
        className="atl-header__trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        {/* Het woord "Portefeuille" staat alleen voor de schermlezer: zichtbaar
            leest het naast "Portefeuille Atlas" in het merkblok als een stotter. */}
        <span className="atl-sr-only">Portefeuille</span>
        <CorporationMark corporation={selected} loading={loading} />
        <ChevronDown size={16} strokeWidth={2} aria-hidden />
      </button>

      {open && (
        <div className="atl-header__popover" role="group" aria-label="Kies een corporatie">
          {corporations.map((option) => {
            const isSelected = option.id === selected?.id;
            return (
              <button
                key={option.id}
                type="button"
                className="atl-header__option"
                aria-current={isSelected}
                onClick={() => {
                  onSelect(option.id);
                  close();
                }}
              >
                <span className="atl-header__option-check" aria-hidden>
                  {isSelected && <Check size={14} strokeWidth={2.5} />}
                </span>
                <span>
                  <strong>{option.name}</strong>
                  <small>{option.asset_count_display}</small>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Wat er in de selector staat: het logo van de corporatie, of anders haar naam.
 *
 * Met logo vervalt de naamregel. Bijna elk corporatielogo is een woordmerk —
 * de naam staat er al in — en dan leest "IJsseldal Wonen" ernaast als een
 * stotter. De naam gaat daarom naar het `alt`-attribuut, waar een schermlezer
 * hem hoe dan ook krijgt. Zonder logo blijft de naam gewoon staan.
 *
 * Bewust geen plaatshouderafbeelding zolang er geen logo is: een grijs vakje
 * met "logo" erin ziet eruit als iets dat kapot is.
 */
export function CorporationMark({
  corporation,
  loading,
}: {
  corporation: Corporation | null;
  loading: boolean;
}) {
  if (loading) return <span className="atl-header__trigger-value">Wordt geladen…</span>;
  if (!corporation) return <span className="atl-header__trigger-value">Geen corporatie</span>;
  if (!corporation.logo_url) {
    return <span className="atl-header__trigger-value">{corporation.name}</span>;
  }
  return (
    <img className="atl-header__logo" src={corporation.logo_url} alt={corporation.name} />
  );
}

/* ---------------------------------------------------------- gebruikersmenu */

function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissable(ref, open, close);

  const { account, label, mode, signOut } = useAuth();
  // Achter `RequireAuth` is er altijd een account; de terugval is er voor het
  // geval dat ooit verandert. Bewust geen fixture-identiteit meer: een
  // verzonnen naam in de header laat een screenshot ingelogd lijken terwijl
  // er niemand is.
  const name = account?.displayName ?? "Onbekende gebruiker";
  const roles = account?.roles ?? [];

  const initials = name
    .split(" ")
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="atl-header__menu atl-header__menu--end" ref={ref}>
      <button
        type="button"
        className="atl-header__trigger atl-header__trigger--user"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="atl-header__avatar" aria-hidden>
          {initials}
        </span>
        <span className="atl-header__trigger-value">{name}</span>
        <ChevronDown size={16} strokeWidth={2} aria-hidden />
      </button>

      {open && (
        <div className="atl-header__popover atl-header__popover--user" role="group" aria-label="Gebruikersmenu">
          <div className="atl-header__identity">
            <UserRound size={16} strokeWidth={1.75} aria-hidden />
            <span>
              <strong>{name}</strong>
              <small>{account?.email ?? account?.subject ?? "geen identiteit bekend"}</small>
            </span>
          </div>

          <div className="atl-header__identity">
            <ShieldCheck size={16} strokeWidth={1.75} aria-hidden />
            <span>
              <strong>{label}</strong>
              <small>
                {roles.length > 0 ? `Rollen: ${roles.join(", ")}` : "Geen rollen toegekend"}
                {account?.roleSource === "platform" && " · gezet in het platform"}
              </small>
            </span>
          </div>

          {/* De modus staat er alleen bij als het níet de echte is: een scherm
              dat op een ontwikkelidentiteit draait, mag daar geen twijfel over
              laten bestaan. */}
          {mode !== "entra" && (
            <p className="atl-header__notice">
              Ontwikkelmodus: deze identiteit komt uit de omgeving, niet uit Entra ID (ADR-003).
            </p>
          )}

          <DensitySwitch />

          {/* De werkvoorraad van opmerkingen. Hier en niet in de zijbalk: die
              gaat over de portefeuille van de corporatie, dit over het platform
              zelf. Voor iedereen zichtbaar — een meelezer ziet er zijn eigen
              opmerkingen met de stand erbij, een schrijfrol ziet ze allemaal. */}
          <Link
            to="/feedback"
            className="atl-header__option atl-header__option--action"
            onClick={close}
          >
            <MessageSquare size={14} strokeWidth={2} aria-hidden />
            Feedback en status
          </Link>

          {/* Rechten aanpassen (29-09-2026): alleen voor een beheerder. De
              backend weigert de lijst voor iedereen anders, dus de link is
              alleen een gemak. */}
          {roles.includes("admin") && (
            <Link
              to="/gebruikers"
              className="atl-header__option atl-header__option--action"
              onClick={close}
            >
              <UsersRound size={14} strokeWidth={2} aria-hidden />
              Gebruikers en rechten
            </Link>
          )}

          <button
            type="button"
            className="atl-header__option atl-header__option--action"
            onClick={() => void signOut()}
          >
            <LogOut size={14} strokeWidth={2} aria-hidden />
            Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Dichtheid Ruim of Compact (ronde 3, 28-10-2026). Onthouden per gebruiker; zonder
 * keuze Compact onder 1440 px breed (`styles/density.ts`).
 */
function DensitySwitch() {
  const [dichtheid, kies] = useDensity();
  return (
    <div className="atl-header__density" role="group" aria-label="Dichtheid van het scherm">
      <Rows3 size={14} strokeWidth={2} aria-hidden />
      <span>Dichtheid</span>
      <Segmented
        label="Dichtheid"
        size="small"
        className="atl-header__density-seg"
        options={[{ value: "ruim", label: "Ruim" }, { value: "compact", label: "Compact" }]}
        value={dichtheid}
        onChange={kies}
      />
    </div>
  );
}
